import { A } from '@solidjs/router';
import { createSignal, createEffect, For, Show, onMount, onCleanup, type Component } from 'solid-js';
import { LOGO, POWERED_BY_MAGICDOOR, SIGN_OUT, TOGGLE_MENUS, SETTINGS } from '~/assets/strings';
import { Avatar } from '~/components/common/Avatar';
import { IconCircleEllipsis } from '~/components/common/icons';
import ImageWithFallback from '~/components/common/images/ImageWithFallback';
import usePresenter from '~/framework/hooks/usePresenter';
import useUseCase from '~/framework/hooks/useUseCase';
import { useStyling } from '~/global-contexts/CompanyStyling';
import { useLocalization } from '~/hooks/useLocalization';
import { useLockBodyScroll } from '~/hooks/useLockBodyScroll';
import { PresentableCompayStyle } from '~/presenters/CompanyStylePresenter';
import { TenantInfoPresenter, type PresentableTenantInfo } from '~/presenters/TenantInfoPresenter';
import { LogOutUseCase } from '~/use-cases/auth/logOutUseCase';
import { GetTenantInfoUseCase } from '~/use-cases/getTenantInfoUseCase';
import { cn } from '~/utils/classnames';
import clickOutside from '~/utils/mouseEvents';
import { useMenu, type MenuItem } from './MenuContext';
import { IconSignOut, IconSettings } from './icons';

const ProfileCard: Component<{ class?: string; tenantInfo?: PresentableTenantInfo; onItemClicked: () => void }> = (props) => {
  const { t } = useLocalization();
  const name = () => (!props.tenantInfo ? '-' : `${props.tenantInfo?.firstName ?? ''} ${props.tenantInfo?.lastName ?? ''}`);
  const { execute: logOut } = useUseCase(LogOutUseCase);

  const handleSignOut = (e: MouseEvent) => {
    e.preventDefault();
    logOut();
    props.onItemClicked();
  };

  return (
    <div class={cn('m-1 rounded-lg', props.class)}>
      <div class="flex flex-col items-center gap-2 p-8">
        <Avatar name={name()} />
        <span class="text-sm font-medium">{name()}</span>
      </div>
      <ul class="divide-y divide-inherit border-t border-inherit text-left text-sm">
        <li>
          <A href="/" onClick={handleSignOut} class="flex cursor-pointer items-center gap-2 px-4 py-5 transition-colors hover:bg-faded/10">
            <IconSignOut class="h-5 w-5" />
            {t(SIGN_OUT)}
          </A>
        </li>
        <li>
          <A
            href="/tenants/settings"
            onClick={() => props.onItemClicked()}
            class="flex cursor-pointer items-center gap-2 px-4 py-5 transition-colors hover:bg-faded/10">
            <IconSettings class="h-5 w-5" />
            {t(SETTINGS)}
          </A>
        </li>
      </ul>
    </div>
  );
};

const Profile: Component<{ class?: string; tenantInfo?: PresentableTenantInfo }> = (props) => {
  const [expanded, setExpanded] = createSignal(false);

  const name = () => {
    if (!props.tenantInfo) return '-';
    return `${props.tenantInfo?.firstName ?? ''} ${props.tenantInfo?.lastName ?? ''}`;
  };

  let container: HTMLDivElement | undefined;

  createEffect(() => {
    if (!expanded() || !container) return;
    const cleanup = clickOutside(container, () => setExpanded(false));
    onCleanup(cleanup);
  });

  return (
    <div
      ref={container}
      class={cn('relative flex items-center gap-3 px-4 py-3 transition-colors aria-expanded:bg-black/20', props.class)}
      aria-expanded={expanded()}>
      <Avatar name={name()} />
      <span class="text-sm font-medium">{name()}</span>
      <button
        class="z-0 ml-auto flex rounded p-2 outline-0 transition-colors hover:bg-white/10"
        type="button"
        onClick={() => setExpanded((prev) => !prev)}>
        <IconCircleEllipsis class="h-5 w-5" />
      </button>
      <Show when={expanded()}>
        <ProfileCard
          tenantInfo={props.tenantInfo}
          class="absolute bottom-0 left-full w-64 bg-white text-secondary-color shadow-lg"
          onItemClicked={() => setExpanded(false)}
        />
      </Show>
    </div>
  );
};

const MenuItems: Component<{ class?: string; menus: MenuItem[]; style?: PresentableCompayStyle }> = (props) => {
  const { t } = useLocalization();
  const { isActive } = useMenu();

  return (
    <ul class={cn('px-3 text-left text-sm text-white', props.class)}>
      <For each={props.menus}>
        {(item) => (
          <li
            class={'my-2 rounded-lg transition-colors hover:bg-hover-color'}
            classList={{ 'bg-active-color font-medium': isActive(item) }}>
            <A class="flex w-full items-center gap-2 p-3" href={item.href}>
              {item.icon && <item.icon active={isActive(item)} />}
              {t(item.text)}
            </A>
          </li>
        )}
      </For>
    </ul>
  );
};

const PCMenu: Component<{ tenantInfo?: PresentableTenantInfo }> = (props) => {
  const { menuItems } = useMenu();
  const { t } = useLocalization();
  const { getCompanyStyling } = useStyling();

  return (
    <div
      class={`sticky top-0 z-[1] hidden h-screen w-64 shrink-0 flex-col text-sm md:flex`}
      style={{ 'background-image': 'linear-gradient(to bottom, var(--gradient-color-start), var(--gradient-color-end)' }}>
      <ImageWithFallback
        src={getCompanyStyling()?.logo}
        class="w-full object-contain object-top p-4"
        alt={t(LOGO)}
        fallback={<div class="m-6 flex justify-center text-2xl text-white">{getCompanyStyling()?.name}</div>}
      />
      <MenuItems menus={menuItems.filter((m) => m.position === 'main')} />
      <Profile class="mt-auto border-t border-white/10 bg-active-color text-white" tenantInfo={props.tenantInfo} />
    </div>
  );
};

const MobileMenu: Component<{ tenantInfo?: PresentableTenantInfo }> = (props) => {
  const { t } = useLocalization();
  const { menuItems, collapsed, toggleCollapsed, isActive } = useMenu();
  const { getCompanyStyling } = useStyling();

  useLockBodyScroll(() => !collapsed());

  return (
    <>
      <div class="fixed inset-0 z-[2] bg-black/50 transition" classList={{ 'opacity-0 hidden': collapsed() }} onClick={toggleCollapsed} />
      <div
        class="fixed inset-y-0 left-0 z-[2] flex w-3/4 flex-col overflow-auto bg-gradient-to-t from-primary-color to-primary-color pt-10 shadow-xl transition md:hidden"
        classList={{ '-translate-x-full': collapsed() }}>
        <Show
          when={getCompanyStyling()?.logo}
          fallback={<div class="m-6 flex justify-center text-2xl text-white">{getCompanyStyling()?.name}</div>}>
          <img
            src={getCompanyStyling()?.logo ?? ''}
            class="mx-auto mt-6 aspect-square w-full overflow-hidden rounded-2xl object-contain object-top"
            alt={t(LOGO)}
          />
        </Show>
        <ul class="divide-y divide-white/10 px-4 py-5 text-left text-white">
          <For each={menuItems}>
            {(item) => (
              <li
                class={'my-2 rounded-lg transition-colors hover:bg-hover-color'}
                classList={{ 'bg-active-color font-medium': isActive(item) }}>
                <A class="flex w-full items-center gap-3 px-3 py-4" href={item.href} onClick={toggleCollapsed}>
                  {item.icon && <item.icon />}
                  {t(item.text)}
                </A>
              </li>
            )}
          </For>
        </ul>
        <ProfileCard
          class="mx-6 mt-auto border border-faded/10 bg-active-color text-white"
          tenantInfo={props.tenantInfo}
          onItemClicked={() => toggleCollapsed()}
        />
        <p class="mb-8 mt-2 text-center text-xs text-white/60">{t(POWERED_BY_MAGICDOOR)}</p>
      </div>
    </>
  );
};

export const Menu = () => {
  const { t } = useLocalization();
  const { collapsed, toggleCollapsed } = useMenu();
  const { execute: fetchTenantInfo } = useUseCase(GetTenantInfoUseCase);
  const { model: tenantInfo } = usePresenter(TenantInfoPresenter);

  onMount(() => {
    fetchTenantInfo();
  });

  return (
    <div>
      <PCMenu tenantInfo={tenantInfo()} />
      <MobileMenu tenantInfo={tenantInfo()} />
      <div
        class="fixed left-4 top-3 z-[3] cursor-pointer rounded-lg bg-gray-100 p-2 md:hidden"
        aria-label={t(TOGGLE_MENUS)}
        aria-expanded={!collapsed()}
        onClick={toggleCollapsed}>
        <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 18L20 18" stroke="#000000" stroke-width="2" stroke-linecap="round" />
          <path d="M4 12L20 12" stroke="#000000" stroke-width="2" stroke-linecap="round" />
          <path d="M4 6L20 6" stroke="#000000" stroke-width="2" stroke-linecap="round" />
        </svg>
      </div>
    </div>
  );
};
